import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PhotoIcon, TrashIcon } from "@heroicons/react/24/outline";
import StepIndicator from "./StepIndicator";
import { useDropzone } from 'react-dropzone';
import { FaUpload, FaTimes, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

const PropertyImageUpload = ({ navigateOnFinish, steps, currentStep = 2, finishButtonText = "Finalizar", finishToastMessage = "Propiedad guardada exitosamente. Puedes solicitar su publicación cuando esté lista." }) => {
    const navigate = useNavigate();
    const { id } = useParams(); // This is now a UUID in the analyze flow
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [images, setImages] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [deletingImage, setDeletingImage] = useState(false);
    const [uploadingImages, setUploadingImages] = useState(new Set());
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const [numericId, setNumericId] = useState(null);
    const [idealistaImport, setIdealistaImport] = useState(null);

    // Resolve UUID to numeric id if needed
    useEffect(() => {
        const resolveId = async () => {
            // If id looks like a UUID, resolve it
            const isUuid = /^[a-f0-9-]{36}$/.test(id);
            if (isUuid) {
                try {
                    const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/wizard/propiedades/by-public-id/${id}`, {
                        credentials: 'include'
                    });
                    if (res.ok) {
                        const data = await res.json();
                        setNumericId(data.id);
                    } else {
                        setError('Propiedad no encontrada');
                    }
                } catch (err) {
                    console.error('Error resolving public_id:', err);
                    setError('Error al cargar la propiedad');
                }
            } else {
                setNumericId(id);
            }
        };
        resolveId();
    }, [id]);

    // Check for pending async Idealista import
    useEffect(() => {
        if (!id) return;
        const stored = localStorage.getItem(`idealista_import_${id}`);
        if (stored) {
            try { setIdealistaImport(JSON.parse(stored)); } catch {}
        }
    }, [id]);

    // Poll for new images while Idealista import is in progress
    useEffect(() => {
        if (!idealistaImport || !numericId) return;

        const poll = async () => {
            try {
                const response = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${numericId}/imagenes`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    const updated = data.map(img => ({ id: img.id, url: img.url_imagen }));
                    setImages(updated);
                    if (updated.length >= idealistaImport.total) {
                        localStorage.removeItem(`idealista_import_${id}`);
                        setIdealistaImport(null);
                    }
                }
            } catch {}
        };

        const interval = setInterval(poll, 3000);
        const timeout = setTimeout(() => {
            clearInterval(interval);
            localStorage.removeItem(`idealista_import_${id}`);
            setIdealistaImport(null);
        }, 5 * 60 * 1000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [idealistaImport, numericId, id]);

    // Fetch existing images when numericId is available
    useEffect(() => {
        if (!numericId) return;
        const fetchExistingImages = async () => {
            try {
                const response = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${numericId}/imagenes`, {
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error('Error al obtener las imágenes');
                }

                const data = await response.json();
                setImages(data.map(img => ({ id: img.id, url: img.url_imagen })));
            } catch (err) {
                console.error('Error fetching images:', err);
                setError('Error al cargar las imágenes existentes');
            }
        };

        fetchExistingImages();
    }, [numericId]);

    const handleImageUpload = async (files) => {
        if (!numericId) {
            setError('La propiedad aún se está cargando. Inténtalo de nuevo en un momento.');
            return;
        }
        setLoading(true);
        setError(null);
        const newUploadingImages = new Set();

        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('images', file);
                newUploadingImages.add(file.name);
            });
            setUploadingImages(newUploadingImages);

            const response = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/wizard/propiedades/${numericId}/upload-images`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if (!response.ok) throw new Error('Error al subir las imágenes');

            const data = await response.json();
            // Update images with the new ones from the response
            setImages(prev => [...prev, ...data.images.map(img => ({ id: img.id, url: img.url_imagen }))]);
        } catch (err) {
            setError('Error al subir las imágenes. Por favor, inténtalo de nuevo.');
            console.error('Error:', err);
        } finally {
            setLoading(false);
            setUploadingImages(new Set());
        }
    };

    const handleFinish = async () => {
        try {
            setLoading(true);
            if (navigateOnFinish) {
                navigateOnFinish(id);
            } else {
                navigate(`/propiedades/${id}/analizar`);
            }
            toast.success(finishToastMessage);
        } catch (error) {
            console.error('Error al finalizar:', error);
            toast.error('Error al finalizar la operación');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (image) => {
        setDeleteConfirmation({
            imageId: image.id,
            imageUrl: image.url
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation) return;
        
        setDeletingImage(true);
        try {
            const response = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${numericId}/imagenes/${deleteConfirmation.imageId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Error al eliminar la imagen');
            }

            setImages(images.filter(img => img.id !== deleteConfirmation.imageId));
            if (previewImage === deleteConfirmation.imageUrl) {
                setPreviewImage(null);
            }
        } catch (error) {
            console.error('Error:', error);
            setError(error.message || 'Error al eliminar la imagen');
        } finally {
            setDeletingImage(false);
            setDeleteConfirmation(null);
        }
    };

    const onDrop = useCallback((acceptedFiles) => {
        handleImageUpload(acceptedFiles);
    }, [numericId]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif']
        },
        maxSize: 10 * 1024 * 1024, // 10MB
        disabled: !numericId
    });

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
            <StepIndicator currentStep={currentStep} steps={steps} propertyId={id} />
            <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Subir imágenes</h1>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">Recomendaciones para las imágenes</h3>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>Sube un mínimo de 4 fotografías</li>
                    <li>Al menos 1 foto por habitación</li>
                    <li>En casas: incluye foto del exterior; si hay piscina o jardín, fotos separadas</li>
                    <li>Incluye pasillos, zaguanes y hall de entrada</li>
                    <li>Fotografía ventanas y puertas para analizar materiales y antigüedad</li>
                    <li>Incluye fotos de las paredes para detectar gotelé u otros acabados</li>
                    <li>Fotografía el cuadro eléctrico, enchufes e interruptores</li>
                    <li>Maximiza la vista general de cada estancia para un mejor análisis</li>
                    <li>Muy recomendable: incluye un plano de la vivienda para mayor precisión</li>
                </ul>
            </div>

            {idealistaImport && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-3">
                    <FaSpinner className="animate-spin text-blue-600 flex-shrink-0" />
                    <p className="text-sm text-blue-700">
                        Importando imágenes de Idealista... ({images.length} de {idealistaImport.total})
                    </p>
                </div>
            )}

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                    {error}
                </div>
            )}

            <div className="space-y-8">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Imágenes
                    </label>
                    <div
                        {...getRootProps()}
                        className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors duration-150 ${
                            !numericId ? 'border-gray-200 bg-gray-50 cursor-not-allowed' :
                            isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-500'
                        }`}
                    >
                        <input {...getInputProps()} />
                        <div className="space-y-1 text-center">
                            {!numericId ? (
                                <>
                                    <FaSpinner className="mx-auto text-4xl text-gray-400 mb-4 animate-spin" />
                                    <p className="text-sm text-gray-500">Cargando propiedad...</p>
                                </>
                            ) : (
                                <>
                                    <FaUpload className="mx-auto text-4xl text-gray-400 mb-4" />
                                    <div className="flex text-sm text-gray-600">
                                        <p className="pl-1">
                                            {isDragActive
                                                ? "Suelta las imágenes aquí..."
                                                : "Arrastra y suelta imágenes aquí, o haz clic para seleccionar"}
                                        </p>
                                    </div>
                                    <p className="text-xs text-gray-500">PNG, JPG, GIF hasta 10MB</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {images.length > 0 && (
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Imágenes subidas ({images.length})</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {images.map((image, index) => (
                                <div key={index} className="relative group">
                                    <div 
                                        className="aspect-square rounded-lg overflow-hidden cursor-pointer relative"
                                        onClick={() => setPreviewImage(image.url)}
                                    >
                                        <img
                                            src={image.url}
                                            alt={`Imagen ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZW4gY2FyZ2FuZG8uLi48L3RleHQ+PC9zdmc+';
                                            }}
                                        />
                                        {uploadingImages.has(image.url) && (
                                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                                <FaSpinner className="animate-spin text-white text-2xl" />
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClick(image);
                                        }}
                                        disabled={deletingImage || uploadingImages.has(image.url)}
                                        className="absolute top-2 right-2 text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
                                        title="Eliminar imagen"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-end pt-6 gap-4">
                    {images.length < 4 && idealistaImport && (
                        <p className="text-sm text-blue-600">
                            Importando imágenes... ({images.length}/{idealistaImport.total})
                        </p>
                    )}
                    {images.length < 4 && !idealistaImport && (
                        <p className="text-sm text-amber-600">
                            Sube al menos 4 imágenes ({images.length}/4)
                        </p>
                    )}
                    <button
                        onClick={handleFinish}
                        disabled={loading || (images.length < 4 && !idealistaImport)}
                        className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition duration-150 ease-in-out"
                    >
                        {loading ? "Subiendo..." : finishButtonText}
                    </button>
                </div>
            </div>

            {previewImage && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="relative max-w-4xl w-full">
                        <button
                            onClick={() => setPreviewImage(null)}
                            className="absolute -top-12 right-0 text-white hover:text-gray-300"
                        >
                            <FaTimes size={24} />
                        </button>
                        <img
                            src={previewImage}
                            alt="Preview"
                            className="w-full h-auto rounded-lg"
                        />
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                        <div className="flex items-center justify-center mb-4">
                            <FaExclamationTriangle className="text-yellow-500 text-4xl" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                            ¿Eliminar imagen?
                        </h3>
                        <p className="text-gray-500 text-center mb-6">
                            Esta acción no se puede deshacer. La imagen se eliminará permanentemente.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setDeleteConfirmation(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deletingImage}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors disabled:opacity-50 flex items-center"
                            >
                                {deletingImage ? (
                                    <>
                                        <FaSpinner className="animate-spin mr-2" />
                                        Eliminando...
                                    </>
                                ) : (
                                    'Eliminar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyImageUpload; 